import { getItemDefinition } from "./catalog.js";
export function areStacksCompatible(left, right) {
    return !left.battery && !right.battery && !left.device && !right.device && !left.garment && !right.garment
        && left.definitionId === right.definitionId
        && left.durabilityCurrent === right.durabilityCurrent
        && left.quality === right.quality;
}
export function stackMassGrams(stack) {
    const definition = getItemDefinition(stack.definitionId);
    if (!definition)
        throw new Error(`Unknown item definition: ${stack.definitionId}`);
    return definition.unitMassGrams * stack.quantity
        + (stack.device?.cells.reduce((sum, cell) => sum + (cell ? stackMassGrams(cell) : 0), 0) ?? 0)
        + (stack.garment?.pockets.reduce((sum, pocket) => sum + pocket.items.reduce((mass, item) => mass + stackMassGrams(item), 0), 0) ?? 0);
}
export function inventoryMassGrams(slots) {
    return slots.reduce((sum, stack) => sum + (stack ? stackMassGrams(stack) : 0), 0);
}
export function usedInventorySlots(slots) {
    return slots.reduce((sum, stack) => sum + (stack ? 1 : 0), 0);
}
export function formatMass(massGrams) {
    if (massGrams < 1_000)
        return `${massGrams} g`;
    const kilograms = massGrams / 1_000;
    return `${kilograms >= 10 ? kilograms.toFixed(1) : kilograms.toFixed(2)}`
        .replace(/\.0$/, "")
        .replace(/(\.\d)0$/, "$1") + " kg";
}
export function validateRuntimeStack(stack, depth = 0, ids = new Set()) {
    if (!stack || typeof stack !== "object" || depth > 4 || ids.has(stack.instanceId))
        return false;
    ids.add(stack.instanceId);
    const definition = getItemDefinition(stack.definitionId);
    if (!definition || !isRuntimeInstanceId(stack.instanceId))
        return false;
    if (!Number.isSafeInteger(stack.quantity) || stack.quantity < 1 || stack.quantity > definition.stackLimit)
        return false;
    if (definition.conditionModel === "DURABILITY") {
        if (!Number.isSafeInteger(stack.durabilityCurrent))
            return false;
        if ((stack.durabilityCurrent ?? 0) < 0 || (stack.durabilityCurrent ?? 0) > (definition.durabilityMax ?? 0))
            return false;
    }
    else if (stack.durabilityCurrent !== undefined)
        return false;
    if (definition.battery) {
        if (!stack.battery || stack.battery.capacityWh !== definition.battery.capacityWh || !Number.isFinite(stack.battery.remainingWh) || stack.battery.remainingWh < 0 || stack.battery.remainingWh > stack.battery.capacityWh)
            return false;
    }
    else if (stack.battery)
        return false;
    if (definition.lightDevice) {
        if (!stack.device || typeof stack.device.on !== 'boolean' || !Array.isArray(stack.device.cells) || stack.device.cells.length !== definition.lightDevice.battery.count)
            return false;
        if (!stack.device.cells.every(cell => cell === null || (validateRuntimeStack(cell, depth + 1, ids) && getItemDefinition(cell.definitionId)?.battery?.format === definition.lightDevice.battery.format)))
            return false;
        if (stack.device.on && stack.device.cells.some(cell => !cell?.battery || cell.battery.remainingWh <= 0))
            return false;
    }
    else if (stack.device)
        return false;
    if (definition.wearable) {
        if (!stack.garment || !Array.isArray(stack.garment.pockets) || stack.garment.pockets.length !== definition.wearable.pockets.length || !stack.garment.closures || typeof stack.garment.closures !== 'object')
            return false;
        if (new Set(stack.garment.pockets.map(p => p.id)).size !== stack.garment.pockets.length)
            return false;
        for (const pocket of stack.garment.pockets) {
            const pocketDef = definition.wearable.pockets.find(p => p.id === pocket.id);
            if (!pocketDef || !Array.isArray(pocket.items) || pocket.items.length > 512 || !pocket.items.every((item) => validateRuntimeStack(item, depth + 1, ids)))
                return false;
            let volume = 0;
            for (const item of pocket.items) {
                const itemDef = getItemDefinition(item.definitionId);
                if (itemDef.wearable || itemDef.longestDimensionMm > pocketDef.maxDimensionMm || (pocketDef.allowedCategories && !pocketDef.allowedCategories.includes(itemDef.category)))
                    return false;
                volume += itemDef.volumeMl * item.quantity;
            }
            if (volume > pocketDef.capacityMl + 1e-9 || (pocket.quickItemId !== null && !isRuntimeInstanceId(pocket.quickItemId)))
                return false;
        }
        if (Object.entries(stack.garment.closures).some(([id, open]) => typeof open !== 'boolean' || !definition.wearable.closures.some(c => c.id === id)) || definition.wearable.closures.some(c => typeof stack.garment.closures[c.id] !== 'boolean'))
            return false;
    }
    else if (stack.garment)
        return false;
    return stack.quality === undefined || ["PRISTINE", "SERVICEABLE", "WORN", "DAMAGED"].includes(stack.quality);
}
export function validateInventoryState(state) {
    return Number.isSafeInteger(state.revision)
        && state.revision >= 0
        && Number.isSafeInteger(state.slotCount)
        && state.slotCount >= 1
        && state.slots.length === state.slotCount
        && state.slots.every((stack) => stack === null || validateRuntimeStack(stack))
        && state.massGrams === inventoryMassGrams(state.slots)
        && (state.maxMassGrams === null || (Number.isSafeInteger(state.maxMassGrams) && state.maxMassGrams >= 0));
}
export function isRuntimeInstanceId(value) {
    return typeof value === "string" && value.length >= 1 && value.length <= 80 && /^[A-Za-z0-9_-]+$/.test(value);
}
